import { useState, useEffect, useMemo } from "react";
import { Search, GripVertical, X } from "lucide-react";
import styles from "./style.module.scss";
import {
  setMappedBanksvalues,
  removeMappedBank,
} from "../../reducers/bankSlice";
import { RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import Mapped from "../../icons/Mapped.svg?react";
import Settings from "../../icons/SettingD.svg?react";
import DeleteD from "../../icons/DeleteD.svg?react";
import Membericon from "../../icons/Team members.svg?react";
import Lead from "../../icons/TeamLead.svg?react";
import Drage from "../../icons/Draganddrop.svg?react";
import APP from "../../icons/Appicon.svg?react";
import CustomSelect from "../Dropdown";
import { PulseLoader } from "react-spinners";
import { loadOrganizationLogos } from "../../utils";

export interface Bank {
  organizationId: number;
  name: string;
  logo: string;
  color: string;
  organizationLogo?: string;
}

interface Member {
  memberId: number;
  name: string;
  email: string;
  role: string;
}

interface TeamLead {
  memberId: number;
  name: string;
  email?: string;
  role: string;
}

interface OrganizationApplication {
  applicationId: number;
  applicationName: string;
}

interface MappedBank extends Bank {
  applicationId?: number[];
  applicationName?: string;
  members?: Member[];
  teamLead?: TeamLead[];
  environment?: string;
}

interface BankMapperProps {
  banks: Bank[];
  fetchOrgDetails: (orgId: number) => Promise<{
    users: Member[];
    applications: OrganizationApplication[];
  }>;
  onMappingChange?: (updatedBanks: MappedBank[]) => void;
  showAICompanion?: boolean;
  preloadLogos?: boolean; // Optional: whether to preload logos on mount
}

const BankMapper: React.FC<BankMapperProps> = ({
  banks,
  fetchOrgDetails,
  onMappingChange,
  showAICompanion,
  preloadLogos = true,
}) => {
  const dispatch = useDispatch();
  const mappedBanks = useSelector((state: RootState) => state.bank.mappedBanks);
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [bankToDelete, setBankToDelete] = useState<MappedBank | null>(null);
  const [selectedBank, setSelectedBank] = useState<MappedBank | null>(null);
  const [draggingBankId, setDraggingBankId] = useState<number | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pendingBanks, setPendingBanks] = useState<number[]>([]);
  const [autoAddedMembers, setAutoAddedMembers] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [getOrganization, setOrgApplication] = useState<
    OrganizationApplication[]
  >([]);
  const [applicationID, setApplicationID] = useState<any[]>([]);
  const [getMember, setMembersList] = useState<Member[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  const [filteredLeadOptions, setFilteredLeadOptions] = useState<any[]>([]);
  const [teamLead, setTeamLead] = useState<any[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [processedBanks, setProcessedBanks] = useState<Bank[]>([]);

  // Process banks to convert logo data to URLs
  useEffect(() => {
    const processBanksWithLogos = async () => {
      if (banks && banks.length > 0 && preloadLogos) {
        const logoMap = await loadOrganizationLogos(banks);
        const banksWithLogos = banks.map((bank) => ({
          ...bank,
          organizationLogo:
            logoMap.get(bank.organizationId) || bank.organizationLogo,
        }));
        setProcessedBanks(banksWithLogos);
      } else {
        setProcessedBanks(banks || []);
      }
    };

    processBanksWithLogos();
  }, [banks, preloadLogos]);

  const filteredBanks = useMemo(
    () =>
      (processedBanks || []).filter(
        (bank) =>
          bank.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !mappedBanks.some((b) => b.organizationId === bank.organizationId) &&
          !pendingBanks.includes(bank.organizationId),
      ),
    [processedBanks, searchTerm, mappedBanks, pendingBanks],
  );

  const handleDragStart = (e: React.DragEvent, bank: Bank) => {
    e.dataTransfer.setData("bank", JSON.stringify(bank));
    setDraggingBankId(bank.organizationId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    setDraggingBankId(null);

    const bankData = e.dataTransfer.getData("bank");
    if (!bankData) return;
    const bank: Bank = JSON.parse(bankData);
    if (
      mappedBanks.find((b) => b.organizationId === bank.organizationId) ||
      pendingBanks.includes(bank.organizationId)
    )
      return;
    setPendingBanks((prev) => [...prev, bank.organizationId]);
    openModal({ ...bank });
  };

  const openModal = async (bank: MappedBank) => {
    try {
      const { users, applications } = await fetchOrgDetails(
        bank.organizationId,
      );
      const { applicationId, applicationName } = bank;
      const appname = Array.isArray(applicationName)
        ? applicationName
        : (applicationName?.split(", ") ?? []);

      const result = applicationId?.map((id, index) => ({
        label: appname[index],
        value: String(id),
      }));
      setMembersList(users || []);
      if (bank.teamLead && bank.teamLead.length > 0) {
        const leadIds = bank.teamLead.map(l => l.memberId);
        setFilteredMembers(users.filter(m => !leadIds.includes(m.memberId)));
      } else {
        setFilteredMembers(users);
      }
      setOrgApplication(applications || []);
      setSelectedBank(bank);
      setApplicationID(result || []);
      setSelectedMembers(bank.members?.map((m) => m.memberId) || []);
      const selected = bank.members?.map(m => m.memberId) || [];

      setFilteredLeadOptions(
        users
          .filter(u => !selected.includes(u.memberId))
          .map(u => ({ label: u.name, value: String(u.memberId) }))
      );

      setTeamLead(
        bank.teamLead?.map((app) => ({
          label: app.name,
          value: String(app.memberId),
        })) || [],
      );
      setTimeout(() => setIsModalVisible(true), 10);
    } catch (error) {
      console.error("Error fetching org details:", error);
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setTimeout(() => {
      if (selectedBank) {
        setPendingBanks((prev) =>
          prev.filter((id) => id !== selectedBank.organizationId),
        );
      }
      setSelectedBank(null);
      setApplicationID([]);
      setSelectedMembers([]);
      setTeamLead([]);
    }, 300);
  };

  const toggleMemberSelection = (memberId: number) => {
    setSelectedMembers(prev => {
      const updated = prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId];
      // filter team-lead options based on selected members
      setFilteredLeadOptions(
        getMember
          .filter(m => !updated.includes(m.memberId))
          .map(m => ({ label: m.name, value: String(m.memberId) }))
      );

      return updated;
    });
  };

  const handleSaveMapping = () => {
    if (
      applicationID.length === 0 ||
      !selectedBank ||
      !teamLead ||
      selectedMembers.length === 0
    )
      return;

    const updatedBank: MappedBank = {
      ...selectedBank,
      applicationId: applicationID.map((a) => Number(a.value)),
      applicationName: applicationID.map((a) => a.label).join(", "),
      members: selectedMembers.map((memberId) => {
        const m = getMember.find((mem) => mem.memberId === memberId);
        return {
          memberId,
          name: m?.name || "Unknown",
          email: m?.email || "",
          role: "Member",
        };
      }),
      teamLead: teamLead.map((lead) => {
        const m = getMember.find((mem) => mem.memberId === Number(lead.value));
        return {
          memberId: Number(lead.value),
          name: m?.name || lead.label || "Unknown",
          email: m?.email || "",
          role: "Lead",
        };
      }),
    };

    const existing = mappedBanks.find(
      (b) => b.organizationId === updatedBank.organizationId,
    );

    const updatedBanks = existing
      ? mappedBanks.map((bank) =>
        bank.organizationId === updatedBank.organizationId
          ? updatedBank
          : bank,
      )
      : [...mappedBanks, updatedBank];

    dispatch(setMappedBanksvalues(updatedBanks));
    setPendingBanks((prev) =>
      prev.filter((id) => id !== selectedBank.organizationId),
    );
    onMappingChange?.(updatedBanks);
    closeModal();
  };

  const handleRemoveBank = (bank: MappedBank) => {
    setBankToDelete(bank);
    setIsDeleteConfirmVisible(true);
  };

  const confirmDeleteBank = () => {
    if (bankToDelete) {
      dispatch(removeMappedBank(bankToDelete.organizationId));
      const updatedBanks = mappedBanks.filter(
        (b) => b.organizationId !== bankToDelete.organizationId,
      );
      onMappingChange?.(updatedBanks);
    }
    setBankToDelete(null);
    setIsDeleteConfirmVisible(false);
  };

  const cancelDeleteBank = () => {
    setBankToDelete(null);
    setIsDeleteConfirmVisible(false);
  };
  const handleorgselection = (selectedValues: any[]) => {
    setApplicationID(selectedValues);
  };
  const handleleadselection = (selectedValues: any[]) => {
    setTeamLead(selectedValues);

    if (selectedValues.length > 0) {
      const leadId = Number(selectedValues[0].value);
      const updatedMembers = getMember.filter(m => m.memberId !== leadId);
      setFilteredMembers(updatedMembers);
      setSelectedMembers(prev =>
        prev.filter(id => id !== leadId)
      );
    } else {
      setFilteredMembers(getMember);
    }
  };


  return (
    <div className={styles.bankMapper}>
      <main className={styles.main}>
        <div className={styles.content}>
          <div
            className={styles.bankLibrary}
            style={{ width: showAICompanion ? "30%" : "25%" }}
          >
            <h4 className={styles.libraryHeader}>Organizations</h4>
            <div className={styles.bankalign}>
              <div className={styles.searchContainer}>
                <Search className={styles.searchIcon} size={18} />
                <input
                  type="text"
                  placeholder="Search organizations"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              <div className={styles.iconalign}>
                <div className={styles.bankGrid}>
                  <div className={styles.grid}>
                    {filteredBanks?.length === 0 ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          marginTop: "1rem",
                          marginLeft: "4rem",
                          padding: "5rem",
                        }}
                        data-testid="loader"
                      >
                        <PulseLoader size={10} color="#4a4a4a" />
                      </div>
                    ) : (
                      filteredBanks.map((bank) => (
                        <div
                          key={bank.organizationId}
                          data-testid={`bank-tile-${bank.organizationId}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, bank)}
                          className={`${styles.bankTile} ${draggingBankId === bank.organizationId
                              ? styles.dragging
                              : ""
                            }`}
                        >
                          <div className={styles.dragHandle}>
                            <GripVertical size={14} />
                          </div>
                          <div className={styles.tileContent}>
                            <div
                              className={styles.logoContainer}
                              style={{ backgroundColor: `${bank.color}15` }}
                            >
                              {bank.organizationLogo && (
                                <img
                                  src={bank.organizationLogo}
                                  alt={`${bank.name} logo`}
                                  className={styles.tabIcon}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              )}
                            </div>
                            <span className={styles.bankName}>{bank.name}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            data-testid="drop-zone"
            className={`${styles.dropZone} ${isDragOver ? styles.dragOver : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <h2 className={styles.dropHeader}>Select Organizations Here</h2>
            {mappedBanks.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <Drage />
                </div>
                <p className={styles.emptyDescription}>
                  Drag organizations from the left panel and drop them here to
                  start creating the audit plan.
                </p>
              </div>
            ) : (
              <div>
                <div className={styles.mappedGrid}>
                  {mappedBanks.map((bank) => (
                    <div
                      key={bank.organizationId}
                      className={styles.mappedBankTile}
                    >
                      <div className={styles.mappedContent}>
                        <div
                          className={styles.mappedLogoContainer}
                          style={{ backgroundColor: `${bank.color}15` }}
                        >
                          {bank.organizationLogo && (
                            <img
                              src={bank.organizationLogo}
                              alt={`${bank.name} logo`}
                              className={styles.tabIcon}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          )}
                        </div>
                        <span className={styles.mappedBankName}>
                          {bank.name}
                        </span>
                      </div>
                      <div className={styles.mappedicon}>
                        {bank.applicationId ? (
                          <>
                            <Mapped /> <span>&nbsp; Selected</span>
                          </>
                        ) : (
                          <span>&nbsp;</span>
                        )}
                      </div>

                      <div className={styles.actions}>
                        <button
                          data-testid={`edit-btn-${bank.organizationId}`}
                          onClick={() => openModal(bank)}
                          className={`${styles.actionButton} ${styles.edit}`}
                          title="Map Application"
                        >
                          <Settings />
                        </button>
                        <button
                          data-testid={`delete-btn-${bank?.organizationId}`}
                          onClick={() => handleRemoveBank(bank)}
                          className={`${styles.actionButton} ${styles.remove}`}
                          title="Remove"
                        >
                          <DeleteD />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      {selectedBank && (
        <>
          <div
            data-testid="modal-overlay"
            role="button"
            tabIndex={0}
            className={`${styles.overlay} ${!isModalVisible ? styles.hidden : ""}`}
            onClick={closeModal}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                closeModal();
              }
            }}
          />

          <div className={styles.modalContainer}>
            <div
              role="button"
              tabIndex={0}
              className={`${styles.modal} ${isModalVisible ? styles.visible : ""}`}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              <div className={styles.modalHeader}>
                <div className={styles.modalHeaderContent}>
                  <div className={styles.modalHeaderText}>
                    <h3 className={styles.modalTitle}>
                      Select Application and Auditee Members
                    </h3>
                    <p className={styles.modalSubtitle}>
                      {selectedBank.organizationLogo && (
                        <img
                          src={selectedBank.organizationLogo}
                          className={styles.tabIcon}
                          alt={`${selectedBank.name} logo`}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}
                      &nbsp;
                      <strong>{selectedBank.name}</strong>
                    </p>
                  </div>
                </div>
                <button
                  data-testid="modal-close-button"
                  onClick={closeModal}
                  className={styles.closeButton}
                >
                  <X size={24} />
                </button>
              </div>

              <div className={styles.modalBody}>
                {/* Application */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <APP /> Application
                  </label>
                  <CustomSelect
                    options={getOrganization.map((app) => ({
                      label: app.applicationName,
                      value: String(app.applicationId),
                    }))}
                    selectContainerClassName={styles.orgselect}
                    placeholder="Select application"
                    multiSelect={true}
                    withSelectAll={true}
                    defaultSelected={applicationID}
                    onChange={(val) => handleorgselection(val)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <Lead /> Team Lead
                  </label>
                  <CustomSelect
                    options={filteredLeadOptions}
                    selectContainerClassName={styles.orgselect}
                    defaultSelected={teamLead}
                    placeholder="Select team lead"
                    onChange={(val) => handleleadselection(val)}
                  />
                </div>
                {/* Team Members */}
                <div
                  className={styles.formGroup}
                  style={{ maxHeight: "150px", overflowY: "auto" }}
                >
                  <div className={styles.memberTable}>
                    <div className={styles.memberHeader}>
                      <div className={styles.memberHeaderItem}>
                        <Membericon className="me-2" /> Team Members
                      </div>
                    </div>

                    {filteredMembers.map((member) => (
                      <div
                        key={member.memberId}
                        className={styles.memberRow}
                        onClick={() => toggleMemberSelection(member.memberId)}
                        onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                     toggleMemberSelection(member.memberId);
                                             }
                                        }}
                                       role="button"
                                       tabIndex={0}
                        style={{ cursor: "pointer" }}
                      >
                        <div className={styles.memberItem}>
                          <input
                            type="checkbox"
                            readOnly
                            checked={selectedMembers.includes(member.memberId)}
                          />
                          <span>{member.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={styles.modalFooter}>
                <div
                  className={styles.tooltipWrapper}
                  onMouseEnter={() => {
                    if (
                      applicationID.length === 0 ||
                      teamLead.length === 0 ||
                      selectedMembers.length === 0
                    ) {
                      setShowTooltip(true);
                    }
                  }}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <button
                    onClick={handleSaveMapping}
                    disabled={
                      applicationID.length === 0 ||
                      teamLead.length === 0 ||
                      selectedMembers.length === 0
                    }
                    className={`${styles.button} ${styles.save}`}
                  >
                    Save
                  </button>

                  {(applicationID.length === 0 ||
                    teamLead.length === 0 ||
                    selectedMembers.length === 0) && (
                    <div
                      className={`${styles.tooltip} ${showTooltip ? styles.visible : ""}`}
                    >
                      {applicationID.length === 0
                        ? "Please select at least one application."
                          : teamLead.length === 0
                            ? "Please assign a team lead."
                             : selectedMembers.length === 0
                               ? "Please select at least one team member."
                                 : ""}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {isDeleteConfirmVisible && (
        <>
          <div
            role="button"
            tabIndex={0}
            className={styles.overlay}
            onClick={cancelDeleteBank}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                cancelDeleteBank();
              }
            }}
          />

          <div className={styles.confirmModalContainer}>
            <div className={styles.confirmModal}>
              <h3 className={styles.confirmTitle}>Confirm Delete</h3>
              <p className={styles.confirmMessage}>
                Are you sure you want to delete this organization?
              </p>

              <div className={styles.confirmActions}>
                <button
                  className={`${styles.confirmButton} ${styles.cancelButton}`}
                  onClick={cancelDeleteBank}
                >
                  Cancel
                </button>
                <button
                  className={`${styles.confirmButton} ${styles.deleteButton}`}
                  onClick={confirmDeleteBank}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BankMapper;
